import { LevelType } from '../types/Level';
import { compareKey, uniqueLetterCount } from './normalize';
import { MAX_KEY_LETTERS, keyLetterSource } from './keyLetters';
import { Candidate, RejectedEntry } from './types';
import { gridLetters } from './gridBuilder';

export interface Identity {
  levelType: LevelType;
  sentence?: string;
  locationName?: string;
  category?: string;
}

export interface Dropped {
  label: string;
  reason: string;
}

// The game's word search levels always hide this many words
export const WORD_SEARCH_WORDS = 6;

export const isCategoryType = (type: LevelType): boolean =>
  type === LevelType.CategoryWords || type === LevelType.WordSearch;

// What makes two levels "the same level" for duplicate checks.
export const identityKey = (item: Identity): string => {
  if (isCategoryType(item.levelType)) return 'cat:' + compareKey(item.category || '');
  if (item.levelType === LevelType.WhereIsThisLocated) {
    return 'loc:' + compareKey(item.locationName || item.sentence || '');
  }
  return 'ans:' + compareKey(item.sentence || '');
};

export const candidateLabel = (item: Identity): string => {
  let label = '';
  if (isCategoryType(item.levelType)) {
    label = item.category || '';
  } else if (item.levelType === LevelType.WhereIsThisLocated) {
    label = item.locationName ? `${item.locationName} (${item.sentence || ''})` : item.sentence || '';
  } else {
    label = item.sentence || '';
  }
  return label.trim() || '(empty)';
};

// Returns the reason a candidate cannot become a level, or null when it can.
export const checkCandidate = (candidate: Candidate): string | null => {
  if (isCategoryType(candidate.levelType)) {
    if (!candidate.category.trim()) return 'Missing category';
    if (candidate.levelType === LevelType.CategoryWords && candidate.words.length < 4) return 'Fewer than 4 words';
  } else {
    if (!candidate.sentence.trim()) return 'Missing answer';
    if (candidate.levelType === LevelType.WhereIsThisLocated && !candidate.locationName.trim()) {
      return 'Missing location name';
    }
  }

  const source = keyLetterSource(candidate);
  if (source !== null) {
    const count = uniqueLetterCount(source);
    if (count > MAX_KEY_LETTERS) return `Uses ${count} different letters (limit ${MAX_KEY_LETTERS})`;
  }

  if (candidate.levelType === LevelType.WordSearch) {
    const seen: Record<string, string> = {};
    for (const word of candidate.words) {
      const letters = gridLetters(word);
      // Anything but a Hebrew letter would become a grid cell of its own
      if (!/^[\u05D0-\u05EA]+$/.test(letters)) return `Word "${word}" has characters that cannot go in the grid`;
      if (letters.length < 2 || letters.length > 8) return `Word "${word}" does not fit an 8×8 grid`;
      // The game counts found words by their letters, so two identical ones could never both be found
      if (seen[letters]) return `Words "${seen[letters]}" and "${word}" are the same in the grid`;
      seen[letters] = word;
    }
    if (candidate.words.length !== WORD_SEARCH_WORDS) {
      return `A word search needs exactly ${WORD_SEARCH_WORDS} words (has ${candidate.words.length})`;
    }
  }

  return null;
};

// Keeps the words that fit together within the key-letter limit, skipping the ones that do not.
export const fitCategoryWords = (words: string[], limit: number = MAX_KEY_LETTERS): string[] => {
  const kept: string[] = [];
  for (const word of words) {
    if (uniqueLetterCount([...kept, word].join('')) <= limit) kept.push(word);
  }
  return kept;
};

// The first six words that can go in a grid: plain Hebrew letters, 2 to 8 long, none repeating another.
const usableSearchWords = (words: string[]): string[] => {
  const usable: string[] = [];
  const seen: Record<string, boolean> = {};
  for (const word of words) {
    const letters = gridLetters(word);
    if (!/^[\u05D0-\u05EA]+$/.test(letters) || letters.length < 2 || letters.length > 8 || seen[letters]) continue;
    seen[letters] = true;
    usable.push(word);
  }
  return usable;
};

// Makes a candidate usable where its spare words allow; otherwise leaves it as given for the checks to explain.
const prepareCandidate = (candidate: Candidate): Candidate => {
  if (candidate.levelType === LevelType.WordSearch) {
    const usable = usableSearchWords(candidate.words);
    return usable.length >= WORD_SEARCH_WORDS ? { ...candidate, words: usable.slice(0, WORD_SEARCH_WORDS) } : candidate;
  }
  if (candidate.levelType !== LevelType.CategoryWords) return candidate;
  const fitted = fitCategoryWords(candidate.words);
  return fitted.length >= 4 && fitted.length < candidate.words.length ? { ...candidate, words: fitted } : candidate;
};

export const takenKeys = (levels: Identity[], rejected: RejectedEntry[], queued: Identity[]): Set<string> => {
  const keys = new Set<string>();
  levels.forEach(level => keys.add(identityKey(level)));
  rejected.forEach(entry => keys.add(entry.key));
  queued.forEach(draft => keys.add(identityKey(draft)));
  return keys;
};

export interface Selection {
  accepted: Candidate[];
  dropped: Dropped[];
  shortfall: number;
}

// Walks the candidates in order, keeping valid non-duplicates until each type's count is met.
export const selectCandidates = (
  candidates: Candidate[],
  counts: Partial<Record<LevelType, number>>,
  taken: Set<string>,
  extraCheck?: (candidate: Candidate) => string | null
): Selection => {
  const used = new Set<string>();
  taken.forEach(key => used.add(key));

  const accepted: Candidate[] = [];
  const dropped: Dropped[] = [];
  const filled: Partial<Record<LevelType, number>> = {};

  for (const proposed of candidates) {
    const candidate = prepareCandidate(proposed);
    const wanted = counts[candidate.levelType] || 0;
    const have = filled[candidate.levelType] || 0;
    if (have >= wanted) continue;

    const reason = checkCandidate(candidate) || (extraCheck ? extraCheck(candidate) : null);
    if (reason) {
      dropped.push({ label: candidateLabel(candidate), reason });
      continue;
    }

    const key = identityKey(candidate);
    if (used.has(key)) {
      dropped.push({ label: candidateLabel(candidate), reason: 'Already used' });
      continue;
    }

    used.add(key);
    accepted.push(candidate);
    filled[candidate.levelType] = have + 1;
  }

  let shortfall = 0;
  (Object.keys(counts) as LevelType[]).forEach(type => {
    shortfall += Math.max(0, (counts[type] || 0) - (filled[type] || 0));
  });

  return { accepted, dropped, shortfall };
};
